import { createMolecule } from '@peruselab/api-client/molecules';
import { createIsomer } from '@peruselab/api-client/isomers';
import type { ExpandedReactionParticipant, ReactionResponse } from '@peruselab/api-client/reactions';
import {
  buildParticipant,
  REACTION_ARROW_KINDS,
  type ParticipantRole,
  type ReactionArrow,
  type ReactionArrowKind,
  type ReactionConditions,
  type ReactionDrawingDocument,
  type ReactionParticipant
} from '@peruselab/structure-editor/chemistry/reactionParticipant';
import {
  pasteAtItsPlace,
  reactionPaper,
  renamed,
  type IdMap
} from '@peruselab/structure-editor/chemistry/reactionPaper';
import {
  containPastedAtoms,
  copyFragment,
  pasteFragment,
  placeAlongside
} from '@peruselab/structure-editor/domain/clipboard';
import { deleteAtom } from '@peruselab/structure-editor/domain/graphTopologyMutations';
import { createEmptyDrawingDocument } from '@peruselab/structure-editor/domain/drawingDocumentFactory';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import { importSmiles } from '@peruselab/structure-editor/chemistry/smilesImport';
import { toChemMoleculeV1 } from '@peruselab/structure-editor/domain/moleculeProjection';
import {
  toIsomerCreatePayload,
  toIsomerRefDepiction
} from '@peruselab/structure-editor/domain/moleculeDrawingProjection';
import { translateAnnotations } from '@peruselab/structure-editor/domain/objectAlignment';
import { croppedToContent } from '@peruselab/structure-editor/rendering/contentExtent';
import { drawnHydrogenIds, remapStructureIds } from '@peruselab/structure-editor/domain/structureIdRemap';
import type { BoxedMolecule, ReactionZone } from '@peruselab/structure-editor/domain/reactionGraph';
import type { Annotation, DrawingDocument } from '@peruselab/structure-editor/model';
import type { ReactionArrowAnnotation } from '../generated/drawing-contract';
import { fieldsFromConditions, type ConditionFields } from './reactionConditions';
import { EMPTY_READING, lookupReagent, type ReactionTextReading, type ReadReagent } from './useReactionTextReading';

/**
 * #705 (ADR 0014 §0 / §5) -- the round trip between the paper and a saved reaction document.
 *
 * Paper -> document: each molecule in a box becomes a participant -- registered as a molecule and
 * an isomer (stereo kept), its role from its box (above the arrow: reagent, or catalyst when
 * marked) -- plus the arrow's kind and the Conditions card. Loose molecules are not in the
 * reaction.
 *
 * Document -> paper: each participant is depicted and laid out around a new arrow -- reactants
 * left, products right, agents above, solvents below -- so the boxes form around them again.
 */

/** The saved arrow for a drawn one: the document's eight kinds as they are; the drawing-only
 *  variants to their nearest (an unequal equilibrium is an equilibrium; the vertical forms
 *  forward / equilibrium). The document keeps the x / slash overlay on a forward arrow only. */
export function documentArrowOf(arrow: Pick<ReactionArrowAnnotation, 'arrowKind' | 'overlay'>): ReactionArrow {
  const kind: ReactionArrowKind = (REACTION_ARROW_KINDS as readonly string[]).includes(arrow.arrowKind)
    ? (arrow.arrowKind as ReactionArrowKind)
    : arrow.arrowKind === 'vertical-up'
      ? 'forward'
      : 'equilibrium';
  const overlay =
    kind === 'forward' && (arrow.overlay === 'x' || arrow.overlay === 'slash') ? arrow.overlay : undefined;
  return overlay ? { kind, overlay } : { kind };
}

const ROLE_ORDER: readonly ParticipantRole[] = ['reactant', 'product', 'reagent', 'catalyst', 'solvent'];

/** A boxed molecule's participant role. */
export function roleOf(zone: ReactionZone, catalyst: boolean): ParticipantRole {
  switch (zone) {
    case 'reactants':
      return 'reactant';
    case 'products':
      return 'product';
    case 'above':
      return catalyst ? 'catalyst' : 'reagent';
    case 'below':
      return 'solvent';
  }
}

/**
 * One molecule of the drawing on its own (its atoms, and the stereo among them) -- #1140: right
 * where it sits on the paper, so its depiction is the author's -- with the ids its copy was given.
 */
function moleculePlaced(document: DrawingDocument, atomIds: readonly string[]): { document: DrawingDocument } & IdMap {
  const fragment = copyFragment(document, atomIds)!;
  const { width, height, coordinates } = document.depiction.coordinateSet;
  return pasteAtItsPlace(
    createEmptyDrawingDocument({ width, height }),
    fragment,
    atomIds.map((id) => coordinates[id]!),
    {
      width,
      height
    }
  );
}

/** The atoms and bonds an annotation refers to: `atomId` / `atomIds` / `bondId` / `bondIds`, the
 *  only references annotations carry (as remapStructureIds reads them). */
function referencesOf(value: unknown, found = { atoms: [] as string[], bonds: [] as string[] }) {
  if (Array.isArray(value)) value.forEach((item) => referencesOf(item, found));
  else if (value && typeof value === 'object') {
    for (const [key, item] of Object.entries(value)) {
      if (key === 'atomId' && typeof item === 'string') found.atoms.push(item);
      else if (key === 'bondId' && typeof item === 'string') found.bonds.push(item);
      else if (key === 'atomIds' && Array.isArray(item)) found.atoms.push(...(item as string[]));
      else if (key === 'bondIds' && Array.isArray(item)) found.bonds.push(...(item as string[]));
      else referencesOf(item, found);
    }
  }
  return found;
}

/**
 * #1140: an arrow's ends as free points where they are drawn -- a reaction's paper keeps its
 * arrows in the canvas, whose ends are points (an end on an atom or bond moves to its place).
 */
function withFreeEnds(annotation: Annotation, document: DrawingDocument): Annotation {
  if (annotation.kind !== 'reaction-arrow' && annotation.kind !== 'curved-arrow') return annotation;
  const { coordinates } = document.depiction.coordinateSet;
  const edges = document.structure.graph.edges;
  const free = (anchor: (typeof annotation)['from']) => {
    if (anchor.kind === 'point') return anchor;
    if ('bondId' in anchor) {
      const edge = edges.find((candidate) => candidate.id === anchor.bondId);
      const a = edge && coordinates[edge.source];
      const b = edge && coordinates[edge.target];
      return a && b ? { kind: 'point' as const, at: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } } : null;
    }
    const at = 'atomId' in anchor ? coordinates[anchor.atomId] : undefined;
    return at ? { kind: 'point' as const, at: { x: at.x, y: at.y } } : null;
  };
  const from = free(annotation.from);
  const to = free(annotation.to);
  return from && to ? ({ ...annotation, from, to } as Annotation) : annotation;
}

/** Shapes, text and arrows with free ends: what a canvas may hold. */
const isCanvasAnnotation = (annotation: Annotation) =>
  annotation.kind === 'shape' ||
  annotation.kind === 'text' ||
  ((annotation.kind === 'reaction-arrow' || annotation.kind === 'curved-arrow') &&
    annotation.from.kind === 'point' &&
    annotation.to.kind === 'point');

export interface PaperReaction {
  readonly document: DrawingDocument;
  readonly molecules: readonly BoxedMolecule[];
  readonly isCatalyst: (atomIds: readonly string[]) => boolean;
  readonly arrow: Pick<ReactionArrowAnnotation, 'arrowKind' | 'overlay'>;
  readonly conditions: ReactionConditions | undefined;
  /** #1137 -- what the texts by the arrow say (reagents with their lookups, conditions, notes). */
  readonly reading?: ReactionTextReading;
}

/** The same name typed twice by the arrow is one reagent. */
function uniqueReagents(reagents: readonly ReadReagent[]): ReadReagent[] {
  const seen = new Set<string>();
  return reagents.filter((reagent) => {
    const key = reagent.name.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** The paper a looked-up reagent's structure is laid out on (it is shown as its text). */
const TEXT_PARTICIPANT_PAPER = { width: 400, height: 300 };

/** #1137: a reagent typed by the arrow, as a participant -- its looked-up structure registered,
 *  shown as the text it was typed as. */
async function textParticipant(reagent: ReadReagent, smiles: string): Promise<ReactionParticipant> {
  const { document } = await importSmiles(smiles, TEXT_PARTICIPANT_PAPER);
  const registered = await createMolecule(toChemMoleculeV1(document));
  const aligned = registered.correspondence ? remapStructureIds(document, registered.correspondence) : document;
  const drawnHydrogens = registered.correspondence ? drawnHydrogenIds(aligned, registered.correspondence) : {};
  const isomer = await createIsomer(
    toIsomerCreatePayload(aligned, registered.id, registered.documentRevision, drawnHydrogens)
  );
  const role: ParticipantRole = reagent.zone === 'below' ? 'solvent' : reagent.catalyst ? 'catalyst' : 'reagent';
  const participant = await buildParticipant(
    registered.document,
    isomer.document,
    isomer.id,
    isomer.documentRevision,
    role,
    TEXT_PARTICIPANT_PAPER
  );
  return { ...participant, shownAs: { kind: 'text', annotationId: reagent.annotationId } };
}

/** The Conditions card's values, each empty one filled from the texts; notes gathered, once each. */
function withTextConditions(
  card: ReactionConditions | undefined,
  reading: ReactionTextReading,
  unfound: readonly string[]
): ReactionConditions | undefined {
  const { temperatureC, pressureAtm, durationS, yieldPercent } = reading.conditions;
  const notes = [...new Set([card?.notes, ...reading.notes, ...unfound].filter((note): note is string => !!note))];
  const temperature =
    card?.temperature ?? (temperatureC !== undefined ? { value: temperatureC, unit: 'celsius' as const } : undefined);
  const pressure =
    card?.pressure ?? (pressureAtm !== undefined ? { value: pressureAtm, unit: 'atm' as const } : undefined);
  const duration =
    card?.duration ?? (durationS !== undefined ? { value: durationS, unit: 'second' as const } : undefined);
  const yieldValue = card?.yieldPercent ?? yieldPercent;
  const merged: ReactionConditions = {
    ...(temperature ? { temperature } : {}),
    ...(pressure ? { pressure } : {}),
    ...(duration ? { duration } : {}),
    ...(yieldValue !== undefined ? { yieldPercent: yieldValue } : {}),
    ...(notes.length ? { notes: notes.join('; ') } : {})
  };
  return Object.keys(merged).length ? merged : undefined;
}

/**
 * The paper's reaction as a `chem-drawing.v11` reaction document, registering each participant
 * molecule and isomer on the way. Throws (with a message for the user) when the reaction is not
 * yet saveable: it needs a reactant and a product. #1140: each participant keeps where it sat, and
 * the paper's arrow, text and shapes go in the canvas, so it opens back as drawn.
 */
export async function reactionDocumentFromPaper(paper: PaperReaction): Promise<ReactionDrawingDocument> {
  const boxed = paper.molecules
    .filter((molecule): molecule is BoxedMolecule & { zone: ReactionZone } => molecule.zone !== null)
    .map((molecule) => ({ molecule, role: roleOf(molecule.zone, paper.isCatalyst(molecule.atomIds)) }))
    .toSorted(
      (a, b) =>
        ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) ||
        a.molecule.bounds.minX - b.molecule.bounds.minX ||
        a.molecule.bounds.minY - b.molecule.bounds.minY
    );
  if (!boxed.some(({ role }) => role === 'reactant') || !boxed.some(({ role }) => role === 'product')) {
    throw new Error('A reaction needs at least one reactant and one product in their boxes.');
  }
  // #1140 (owner): what's kept is the drawing's own area -- the structures left out (in no box) go,
  // and the paper is cut down to what remains, with a small margin.
  let kept = paper.document;
  for (const atomId of paper.molecules.filter((molecule) => molecule.zone === null).flatMap((m) => m.atomIds)) {
    kept = deleteAtom(kept, atomId);
  }
  const document = croppedToContent(kept);

  // #1140 (ADR 0025 D3): the paper travels with the reaction. An annotation on one participant's
  // atoms goes with that participant; the rest -- the arrow, text, shapes -- stay on the canvas.
  const bondEnds = new Map(document.structure.graph.edges.map((edge) => [edge.id, [edge.source, edge.target]]));
  const ownerOf = (annotation: Annotation) => {
    if (annotation.kind === 'reaction-arrow') return -1;
    const { atoms, bonds } = referencesOf(annotation);
    const touched = [...atoms, ...bonds.flatMap((id) => bondEnds.get(id) ?? [])];
    if (touched.length === 0) return -1;
    return boxed.findIndex(({ molecule }) => touched.every((id) => molecule.atomIds.includes(id)));
  };
  const owners = document.annotations.map(ownerOf);

  const participants: ReactionParticipant[] = [];
  for (const [index, { molecule, role }] of boxed.entries()) {
    const placed = moleculePlaced(document, molecule.atomIds);
    const own = document.annotations
      .filter((_, at) => owners[at] === index)
      .map((annotation) => renamed(annotation, placed));
    const alone = { ...placed.document, annotations: own };
    const registered = await createMolecule(toChemMoleculeV1(alone));
    // #615: a reused Molecule numbered differently -- the Isomer's stereo must address its atoms.
    const aligned = registered.correspondence ? remapStructureIds(alone, registered.correspondence) : alone;
    // #951: hydrogens drawn as atoms that the reused Molecule holds as implicit stay on the paper.
    const drawnHydrogens = registered.correspondence ? drawnHydrogenIds(aligned, registered.correspondence) : {};
    const isomer = await createIsomer(
      toIsomerCreatePayload(aligned, registered.id, registered.documentRevision, drawnHydrogens)
    );
    participants.push({
      isomerId: isomer.id,
      isomerRevision: isomer.documentRevision,
      role,
      depiction: toIsomerRefDepiction(aligned, isomer.id, isomer.documentRevision, drawnHydrogens),
      annotations: aligned.annotations
    });
  }
  const { width, height } = document.depiction.coordinateSet;
  const canvas = document.annotations
    .filter((_, at) => owners[at] === -1)
    .map((annotation) => withFreeEnds(annotation, document))
    .filter(isCanvasAnnotation);
  // #1137 (ADR 0014 amendment): the texts by the arrow -- each reagent found becomes a participant
  // shown as its text; one not found, and the words for how, are notes; their conditions fill
  // what the Conditions card leaves empty.
  const reading = paper.reading ?? EMPTY_READING;
  const unfound: string[] = [];
  for (const reagent of uniqueReagents(reading.reagents)) {
    const lookup = reagent.lookup.status === 'looking' ? await lookupReagent(reagent.name) : reagent.lookup;
    if (lookup.status !== 'found') {
      unfound.push(reagent.name);
      continue;
    }
    participants.push(await textParticipant(reagent, lookup.smiles));
  }
  const conditions = withTextConditions(paper.conditions, reading, unfound);
  return {
    schemaVersion: 'chem-drawing.v11',
    kind: 'reaction',
    participants,
    arrow: documentArrowOf(paper.arrow),
    ...(conditions ? { conditions } : {}),
    // #1141 (owner; ADR 0025 D7): the reaction's data, not the paper it was drawn on -- no page.
    // It opens on the reader's own page.
    canvas: { width, height, annotations: canvas }
  };
}

export interface ReactionOnPaper {
  readonly document: DrawingDocument;
  /** The atoms of every catalyst participant (for the reagent / catalyst marks). */
  readonly catalystAtomIds: readonly string[];
  readonly conditions: ConditionFields;
}

/**
 * A saved reaction on a fresh paper -- as drawn (#1140), or laid out for an older one
 * (reactionPaper) -- with its Conditions card filled in.
 */
export async function paperFromReaction(
  reaction: ReactionResponse & { readonly participants: readonly ExpandedReactionParticipant[] },
  paperSize: PaperSize
): Promise<ReactionOnPaper> {
  const { document, catalystAtomIds } = await reactionPaper(reaction, paperSize);
  return { document, catalystAtomIds, conditions: fieldsFromConditions(reaction.document.conditions) };
}

export interface PlacedReaction {
  readonly document: DrawingDocument;
  /** Every atom of the placed reaction (for its boxes). */
  readonly reactionAtomIds: readonly string[];
  /** Its catalysts' atoms, as ids in `document`. */
  readonly catalystAtomIds: readonly string[];
}

/**
 * #744 -- puts a saved reaction (laid out by paperFromReaction) on the current paper, as one step:
 * the paper's own reaction, when it has one -- its arrow and the molecules in its boxes -- makes way
 * (one arrow per drawing, ADR 0014 decision b); loose molecules stay. On a paper left empty the
 * reaction is laid down as it is; otherwise beside what is drawn, at its own size.
 */
export function placeReactionOnPaper(
  current: DrawingDocument,
  replace: { readonly arrowId: string | null; readonly atomIds: readonly string[] },
  laid: ReactionOnPaper,
  margin: number
): PlacedReaction {
  let base: DrawingDocument = replace.arrowId
    ? { ...current, annotations: current.annotations.filter((annotation) => annotation.id !== replace.arrowId) }
    : current;
  for (const atomId of replace.atomIds) base = deleteAtom(base, atomId);

  const laidAtomIds = laid.document.structure.graph.nodes.map((node) => node.id);
  if (base.structure.graph.nodes.length === 0 && base.annotations.length === 0) {
    return { document: laid.document, reactionAtomIds: laidAtomIds, catalystAtomIds: laid.catalystAtomIds };
  }

  const fragment = copyFragment(laid.document, laidAtomIds);
  if (!fragment) return { document: base, reactionAtomIds: [], catalystAtomIds: [] };
  const pasted = pasteFragment(base, fragment, placeAlongside(base, fragment));
  const contained = containPastedAtoms(pasted.document, pasted.pastedAtomIds, margin);
  // The fragment's atoms, in order, became pastedAtomIds; all moved by the same offset (a fragment
  // keeps its own size), so the arrow follows the first of them.
  const idOf = new Map(Object.keys(fragment.atoms).map((oldId, index) => [oldId, pasted.pastedAtomIds[index]!]));
  const [firstOld] = Object.keys(fragment.atoms);
  const from = laid.document.depiction.coordinateSet.coordinates[firstOld!]!;
  const to = contained.depiction.coordinateSet.coordinates[idOf.get(firstOld!)!]!;
  const shift = (point: { readonly x: number; readonly y: number }) => ({
    x: point.x + to.x - from.x,
    y: point.y + to.y - from.y
  });
  // #1140: everything on the reaction's paper comes along -- the arrow, text and shapes moved with
  // the molecules, and annotations on atoms renamed to the pasted atoms.
  const offset = shift({ x: 0, y: 0 });
  const moved = translateAnnotations(
    laid.document,
    new Set(laid.document.annotations.map((annotation) => annotation.id)),
    offset.x,
    offset.y
  ).annotations.map((annotation) =>
    renamed(annotation, {
      atoms: idOf,
      bonds: new Map(Object.keys(fragment.bonds).map((oldId, index) => [oldId, pasted.pastedBondIds[index]!]))
    })
  );
  return {
    document: { ...contained, annotations: [...contained.annotations, ...moved] },
    reactionAtomIds: pasted.pastedAtomIds,
    catalystAtomIds: laid.catalystAtomIds.flatMap((id) => (idOf.has(id) ? [idOf.get(id)!] : []))
  };
}
